import { useEffect } from "react";
import type { PricingAgentItem } from "../utils/types";

type PricingAgentPaneProps = {
  open: boolean;
  onClose: () => void;
  companyName?: string;
  pricing?: PricingAgentItem[];
};

const toTitleCase = (value?: string): string =>
  (value || "")
    .split(" ")
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");

const formatValue = (currency?: string, value?: number, unit?: string): string => {
  if (value === undefined || value === null) return "-";
  return `${currency ? `${currency} ` : ""}${value}${unit ? `/${unit}` : ""}`;
};

const PricingAgentPane = ({ open, onClose, companyName, pricing = [] }: PricingAgentPaneProps) => {
  useEffect(() => {
    if (!open) return undefined;
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [open, onClose]);

  return (
    <>
      {open ? <div className="tw-fixed tw-inset-0 tw-z-[9999]" onClick={onClose} /> : null}
      <div className={`tw-fixed tw-top-0 tw-right-0 tw-h-full tw-my-12 tw-bg-white tw-shadow-sideDrawer tw-border-t tw-border-l tw-border-b tw-border-solid tw-border-sideDrawer tw-transition-all tw-duration-250 tw-ease-in-out tw-rounded-bl-[12px] tw-z-[10000] ${open ? "tw-translate-x-0" : "tw-w-0 tw-translate-x-full"}`}>
        {open ? (
          <div className="tw-relative tw-px-5 tw-py-[18px] tw-text-sm">
            <div className="tw-absolute -tw-top-[11px] -tw-left-[11px] tw-z-[1000] tw-cursor-pointer tw-rounded-full tw-bg-white tw-border tw-border-solid tw-border-gray-300 tw-leading-none tw-p-1" onClick={onClose}>
              <svg xmlns="http://www.w3.org/2000/svg" height="20px" viewBox="0 -960 960 960" width="20px" fill="#101828">
                <path d="M291-253.85 253.85-291l189-189-189-189L291-706.15l189 189 189-189L706.15-669l-189 189 189 189L669-253.85l-189-189-189 189Z" />
              </svg>
            </div>

            <p className="tw-mb-4 tw-w-[360px] tw-text-sm tw-font-semibold tw-leading-[1.43] tw-text-gray-900">
              {`Pricing breakdown of ${companyName ? toTitleCase(companyName) : "Unknown"}`}
              {pricing[0]?.product_keyword ? (
                <>
                  &nbsp;for&nbsp;<span className="tw-capitalize">{`${toTitleCase(pricing[0].product_keyword)}`}</span>
                </>
              ) : null}
            </p>

            {pricing.length ? (
              <div className="tw-flex tw-w-[360px] tw-flex-col tw-gap-3 tw-h-[calc(100vh-142px)] tw-overflow-auto tw-pr-3">
                {pricing.map((item) => (
                  <div key={`${item.mode_of_transport || "pricing"}-${item.currency || ""}-${item.average_value ?? ""}-${item.minimum_value ?? ""}-${item.maximum_value ?? ""}`} className="tw-rounded-lg tw-border tw-border-solid tw-border-gray-200 tw-bg-white tw-p-4">
                    <div className="tw-flex tw-flex-col tw-gap-2">
                      <div className="tw-flex tw-items-center tw-justify-between">
                        <span className="tw-text-gray-500">HS Code</span>
                        <span className="tw-font-medium tw-text-gray-900">{item.hs_code || "-"}</span>
                      </div>
                      <div className="tw-flex tw-items-center tw-justify-between">
                        <span className="tw-text-gray-500">Mode of transport</span>
                        <span className="tw-font-medium tw-text-gray-900 tw-capitalize">{item.mode_of_transport || "-"}</span>
                      </div>
                      <div className="tw-flex tw-items-center tw-justify-between">
                        <span className="tw-text-gray-500">Average</span>
                        <span className="tw-font-medium tw-text-gray-900">{formatValue(item.currency, item.average_value, item.quantity_unit)}</span>
                      </div>
                      <div className="tw-flex tw-items-center tw-justify-between">
                        <span className="tw-text-gray-500">Minimum</span>
                        <span className="tw-font-medium tw-text-gray-900">{formatValue(item.currency, item.minimum_value, item.quantity_unit)}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="tw-flex tw-w-[360px] tw-items-center tw-justify-center tw-py-10 tw-text-sm tw-text-gray-400">No pricing available</div>
            )}
          </div>
        ) : null}
      </div>
    </>
  );
};

export default PricingAgentPane;
